import { supabase } from "@/integrations/supabase/client";
import { resolveChallengeId, mapChallengeIdsToCourseIds } from "@/services/courseIdResolver";
import { handleError } from "@/lib/errorHandler";
import { getCurrentUserId } from "@/lib/authCache";

export const courseAccessService = {
  hasAccessToCourse: async (courseOrChallengeId: string, userId?: string): Promise<boolean> => {
    try {
      const targetUserId = userId ?? (await getCurrentUserId()) ?? undefined;
      if (!targetUserId) return false;

      const challengeId = await resolveChallengeId(courseOrChallengeId);
      if (!challengeId) return false;

      const { data, error } = await supabase
        .from("user_course_access")
        .select("id")
        .eq("user_id", targetUserId)
        .eq("challenge_id", challengeId)
        .eq("is_active", true)
        .single();

      if (error && error.code !== "PGRST116") return false;
      return !!data;
    } catch (err) {
      handleError(err, { context: "course_access_check", category: "challenge", silent: true, details: { courseOrChallengeId } });
      return false;
    }
  },

  /**
   * Returns courses.id values that the user has active access to.
   * The library UI works with courses.id, so we reverse-map from
   * challenge_id (used in user_course_access) back to courses.id.
   */
  getAccessibleCourseIds: async (userId?: string): Promise<string[]> => {
    try {
      const targetUserId = userId ?? (await getCurrentUserId()) ?? undefined;
      if (!targetUserId) return [];

      const { data, error } = await supabase
        .from("user_course_access")
        .select("challenge_id")
        .eq("user_id", targetUserId)
        .eq("is_active", true);

      if (error) return [];
      const challengeIds = data?.map(item => item.challenge_id).filter(Boolean) || [];
      if (challengeIds.length === 0) return [];

      const challengeToCourse = await mapChallengeIdsToCourseIds(challengeIds);
      return Object.values(challengeToCourse);
    } catch (err) {
      handleError(err, { context: "course_access_list", category: "challenge", silent: true });
      return [];
    }
  },

  /**
   * The raw challenge ids the user has active access to — NO reverse mapping.
   *
   * Deliberately not `getAccessibleCourseIds`. That one maps back to `courses.id`
   * because the older Library UI works in that id space, and the mapping DROPS
   * any challenge with no legacy `courses` row — which is almost all of them,
   * since `course_id` is NULL on every one of the 13,541 `user_course_access`
   * rows in production.
   *
   * The storefront compares against `course_products.challenge_id`, so it needs
   * this id space. Using the mapped list there would report an owned course as
   * unowned and offer to sell it to someone who already paid.
   */
  getAccessibleChallengeIds: async (userId?: string): Promise<string[]> => {
    try {
      const targetUserId = userId ?? (await getCurrentUserId()) ?? undefined;
      if (!targetUserId) return [];

      const { data, error } = await supabase
        .from("user_course_access")
        .select("challenge_id")
        .eq("user_id", targetUserId)
        .eq("is_active", true);

      if (error) return [];
      return (data ?? []).map((row) => row.challenge_id).filter(Boolean) as string[];
    } catch (err) {
      handleError(err, { context: "course_access_challenge_list", category: "challenge", silent: true });
      return [];
    }
  },

  grantManualAccess: async (
    userId: string,
    courseOrChallengeId: string,
    notes?: string
  ): Promise<void> => {
    const challengeId = await resolveChallengeId(courseOrChallengeId);
    if (!challengeId) throw new Error("course_not_mapped_to_challenge");

    const { error } = await supabase.rpc("grant_course_access", {
      p_user_id: userId,
      p_challenge_id: challengeId,
      p_reason: notes || null,
      p_source: "manual",
    });
    if (error) throw error;
  },

  revokeAccess: async (
    userId: string,
    courseOrChallengeId: string,
    reason?: string
  ): Promise<void> => {
    const challengeId = await resolveChallengeId(courseOrChallengeId);
    if (!challengeId) throw new Error("course_not_mapped_to_challenge");

    const { error } = await supabase.rpc("revoke_course_access", {
      p_user_id: userId,
      p_challenge_id: challengeId,
      p_reason: reason || null,
    });
    if (error) throw error;
  },

  getCourseAccesses: async (courseOrChallengeId: string) => {
    const challengeId = await resolveChallengeId(courseOrChallengeId);
    if (!challengeId) return [];

    const { data, error } = await supabase
      .from("user_course_access")
      .select(`
        *,
        profiles:user_id!inner (
          id,
          display_name,
          email
        )
      `)
      .eq("challenge_id", challengeId)
      .eq("is_active", true)
      .order("granted_at", { ascending: false });

    if (error) throw error;
    return (data || []).filter((row: { profiles: unknown }) => row.profiles != null);
  },

  searchUsers: async (query: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, email")
      .or(`email.ilike.%${query}%,display_name.ilike.%${query}%`)
      .limit(10);

    if (error) throw error;
    return data || [];
  },
};
